/**
 * Role-Permission Mapping
 *
 * Maps admin roles to default permission actions.
 * Domain-level capabilities (formerly steward, criteria-authority, facilitator,
 * cross-map-lead, list-owner, strategist) are now assigned directly to users
 * via CapabilityId permissions in role_permissions. This file only covers
 * admin scope roles.
 */

import type { ActorRole } from './roles';
import type { PermissionAction } from './permissions';
import {
  getCapabilitiesForRole,
  getCapabilitiesForAction,
  getActionsGrantedByCapability,
  getBundleKeyForDbRole,
  type CapabilityId,
} from './capabilityIds';
import { ACTOR_ROLE_PERMISSIONS as ACTOR_ROLE_PERMISSIONS_DATA } from './actorRolePermissions';

/** Actor role → permission actions (admin roles). Re-exported with typed shape for consumers. */
export const ACTOR_ROLE_PERMISSIONS: Record<ActorRole, PermissionAction[]> = ACTOR_ROLE_PERMISSIONS_DATA as Record<ActorRole, PermissionAction[]>;

// ============================================================================
// Navigation Section Access
// ============================================================================

/**
 * Maps navigation sections to required permissions.
 * A user needs at least ONE of the listed permissions to see the section.
 */
// v2.2 - Simplified nav sections (object-centered)
export type NavSectionId =
  | 'home'
  | 'my-tasks'
  | 'intake'
  | 'items'          // Consolidated: items, shaping, scoring, leveling
  | 'relationships'  // Relationship views: clusters, packages, dependencies, etc.
  | 'cycles'         // Primary workspace
  | 'cycle-design'   // Cycle design configuration
  | 'cycle-execution' // Cycle execution workspace
  | 'governance'     // Proposals and ratification
  | 'priorities'     // Published outputs
  | 'settings'
  // Legacy sections (kept for backward compatibility during transition)
  | 'shape'
  | 'scoring'
  | 'leveling'
  | 'sessions'
  | 'lists'
  | 'reports'
  | 'tools'
  | 'monitor'
  | 'decisions'      // Decisions, Decision Intel, Calibration, Bias Audit, Judgment
  | 'strategy';

export const NAV_SECTION_PERMISSIONS: Record<NavSectionId, PermissionAction[]> = {
  // v2.2 Primary sections
  'home': ['items.view', 'session.participate'],  // All workspace members (workspace-member grants session.participate)
  'my-tasks': ['items.view', 'session.participate'],  // All workspace members
  'intake': ['items.view'],  // Everyone can see intake (submit ideas requires items.create, checked per-item)
  'items': ['items.view'],  // Consolidated items section - everyone can view, edit checks per-action
  'relationships': ['items.view'],  // Relationship views — same access as items
  'cycles': ['process.view'],  // Primary workspace
  'cycle-design': ['process.view'],  // Cycle design configuration
  'cycle-execution': ['process.view'],  // Cycle execution workspace
  'governance': ['process.view'],  // Proposals and ratification
  'priorities': ['lists.view', 'process.view'],  // Published prioritization artifacts / priorities view
  'settings': ['admin.users', 'admin.groups', 'admin.roles', 'admin.policies', 'admin.settings'],
  // Legacy sections (kept for backward compatibility)
  'shape': ['items.edit'],  // Only shapers
  'scoring': ['items.edit', 'session.facilitate'],  // Item attribute value entry
  'leveling': ['items.edit', 'session.facilitate'],  // Altitude alignment
  'sessions': ['session.view', 'session.participate', 'session.facilitate'],  // Prioritization sessions
  'lists': ['lists.view'],
  'reports': ['analytics.view', 'process.view'],  // Reports and analytics
  'tools': ['process.prioritize', 'session.facilitate'],
  'monitor': ['analytics.view', 'lists.view'],
  'decisions': ['analytics.view', 'lists.view'],  // Moved out of Reports (monitor) — same rule
  'strategy': ['analytics.view', 'process.view'],
};

/**
 * Maps individual nav items to required permissions.
 */
export const NAV_ITEM_PERMISSIONS: Record<string, PermissionAction[]> = {
  // v2.2 Items section (consolidated)
  '/prioritization/items': ['items.view'],
  '/prioritization/items/needs-definition': ['items.edit'],
  '/prioritization/items?filter=needs-values': ['items.edit', 'values.propose'],
  '/prioritization/items?filter=needs-leveling': ['items.edit'],
  
  // Intake section
  '/prioritization/intake/submit': ['items.create'],
  '/prioritization/intake/mine': ['items.view'],
  '/prioritization/intake/triage': ['items.triage'],
  '/prioritization/intake/queue': ['items.triage'],
  
  // Cycles section
  '/prioritization/cycles': ['process.view'],
  '/prioritization/cascade': ['process.view'],
  '/prioritization/catchball': ['process.view'], // legacy tree still resolves
  
  // Published lists (priorities)
  '/prioritization/plan-of-record': ['lists.view'],
  '/planning/plans': ['lists.view'],
  '/prioritization/settings/csv-citizenship': ['admin.settings'],

  // Legacy Shape section (for backward compatibility)
  '/prioritization/shape/queue': ['items.edit'],
  '/prioritization/shape/attributes': ['criteria.view'],
  '/prioritization/shape/items': ['items.view'],
  '/prioritization/shape/clusters': ['items.edit'],
  
  // Legacy Scoring section
  '/prioritization/scoring': ['items.edit', 'session.facilitate'],
  
  // Legacy Leveling section
  '/prioritization/leveling': ['items.edit', 'session.facilitate'],
  '/prioritization/leveling/grid': ['items.edit', 'session.facilitate'],
  '/prioritization/leveling/constraints': ['items.edit', 'session.facilitate'],
  
  // Legacy Sessions (prioritization sessions)
  '/prioritization/sessions': ['session.view', 'session.participate', 'session.facilitate'],
  
  // Legacy Lists
  '/prioritization/lists': ['lists.view'],
  
  // Legacy Tools (all require prioritization permission)
  '/prioritization/tools': ['process.prioritize', 'session.facilitate'],
  
  // Legacy Monitor section
  '/prioritization/monitor/drift': ['lists.view', 'analytics.view'],
  '/prioritization/monitor/map': ['lists.view'],
  '/prioritization/monitor/analytics': ['analytics.view'],
  
  // Legacy Strategy section
  '/prioritization/strategy/objectives': ['process.view', 'analytics.view'],
  '/prioritization/strategy/alignment': ['lists.view', 'analytics.view'],
  '/prioritization/strategy/opportunity-tree': ['process.view', 'lists.view'],
  '/prioritization/strategy/decisions': ['process.view'],
  
  // Settings section (admin only)
  '/prioritization/settings/users': ['admin.users'],
  '/prioritization/settings/policies': ['admin.policies'],
  '/prioritization/settings/org-hierarchy': ['admin.settings'],
  '/prioritization/settings/queues': ['admin.settings'],
  '/prioritization/settings/identity': ['admin.settings'],
  '/prioritization/settings/connectors': ['admin.settings'],
  '/prioritization/settings/settings-transfer': ['admin.settings'],
  '/prioritization/settings/security': ['admin.settings'],
  '/prioritization/settings/goals': ['admin.settings'],
  
  // Platform Admin - only platform-admin/system-admin
  '/platform-admin': ['admin.policies'],
  '/super-admin': ['admin.policies'],
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Normalize a role string (e.g. from DB or impersonation) to a valid ActorRole.
 * Uses canonical mapping (getBundleKeyForDbRole / DB_ROLE_NAME_TO_BUNDLE_KEY) so DB
 * variants (e.g. workspace_admin, Workspace Administrator) resolve to the same ActorRole
 * as capability resolution. Returns null for non-admin roles (use capabilities instead).
 */
export function toActorRole(role: string): ActorRole | null {
  if (Object.prototype.hasOwnProperty.call(ACTOR_ROLE_PERMISSIONS, role)) {
    return role as ActorRole;
  }
  const bundleKey = getBundleKeyForDbRole(role, null);
  if (bundleKey && Object.prototype.hasOwnProperty.call(ACTOR_ROLE_PERMISSIONS, bundleKey)) {
    return bundleKey as ActorRole;
  }
  return null;
}

/**
 * Check if a role has a specific capability (fallback when DB role_permissions not used, e.g. cycle/session role name).
 */
export function roleHasCapability(role: ActorRole | string, capabilityId: CapabilityId): boolean {
  const caps = getCapabilitiesForRole(typeof role === 'string' ? role : role);
  return caps.includes(capabilityId);
}

/**
 * Check if a role has a specific permission (capability-based: action is granted if role has any capability that grants it).
 */
export function roleHasPermission(role: ActorRole, action: PermissionAction): boolean {
  const caps = getCapabilitiesForRole(role);
  const required = getCapabilitiesForAction(action);
  if (required.length === 0) return false;
  return required.some((c) => caps.includes(c));
}

/**
 * Check if a role has ANY of the specified permissions (capability-based).
 */
export function roleHasAnyPermission(role: ActorRole, actions: PermissionAction[]): boolean {
  return actions.some((action) => roleHasPermission(role, action));
}

/**
 * Check if a role has ALL of the specified permissions (capability-based).
 */
export function roleHasAllPermissions(role: ActorRole, actions: PermissionAction[]): boolean {
  return actions.every((action) => roleHasPermission(role, action));
}

/**
 * Get all permissions (PermissionAction) for a role (derived from capability bundle).
 * Used when cycle/session provides a role name and we need to check actions without DB.
 */
export function getRolePermissions(role: ActorRole): PermissionAction[] {
  const caps = getCapabilitiesForRole(role);
  const actions = new Set<PermissionAction>();
  for (const cap of caps) {
    for (const a of getActionsGrantedByCapability(cap)) actions.add(a);
  }
  return [...actions];
}

/**
 * Check if a role can access a navigation section
 */
export function roleCanAccessNavSection(role: ActorRole, sectionId: NavSectionId): boolean {
  const requiredPermissions = NAV_SECTION_PERMISSIONS[sectionId];
  // Defensive check: if no permissions defined, allow access (fail-open for new sections)
  if (!requiredPermissions || !Array.isArray(requiredPermissions)) {
    console.warn(`[roleCanAccessNavSection] No permissions defined for section: ${sectionId}`);
    return true;
  }
  return roleHasAnyPermission(role, requiredPermissions);
}

/**
 * Check if a role can access a specific nav item by path
 */
export function roleCanAccessNavItem(role: ActorRole, path: string): boolean {
  const requiredPermissions = NAV_ITEM_PERMISSIONS[path];
  if (!requiredPermissions) {
    // If no specific permissions defined, allow access
    return true;
  }
  return roleHasAnyPermission(role, requiredPermissions);
}

/**
 * Get filtered nav items based on role
 */
export function getAccessibleNavItems<T extends { href: string }>(
  role: ActorRole,
  items: T[]
): T[] {
  return items.filter(item => roleCanAccessNavItem(role, item.href));
}
